import { FC, ReactNode, useEffect, useMemo } from 'react';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { useFormContext } from 'react-hook-form';
import { Checkbox } from '@dynamika-shared/ui/Checkbox/Checkbox';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { ScopeItemsList } from './ScopeItemList';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { useLocation } from 'react-router-dom';
import { TaskState } from '../../TaskDefinitionForm';
import { VisitConfig } from '@dynamika-shared/types/visits';

type TaskScopeProps = {
  className?: string;
  children?: ReactNode;
};

type ScopeType = '.Patient' | '.Visit';

const scopesFactory = (type: ScopeType) => {
  const scopesTypes = {
    ['.Patient']: { type: '.Patient', cohorts: [] },
    ['.Visit']: {
      type: '.Visit',
      unscheduled: false,
      allRegular: false,
      visitDefinitions: [],
    },
  };
  return scopesTypes[type];
};

const scopes = [
  { value: '.Patient', label: 'Patient Level' },
  { value: '.Visit', label: 'Visit Level' },
];

export const TaskScope: FC<TaskScopeProps> = () => {
  const location = useLocation();
  const { formType } = location.state as TaskState;

  const { setValue, watch } = useFormContext();
  const [cohorts] = useClinicalTrialSlice(state => [state.data?.cohorts || []]);

  const scope = watch('scope');

  const scopeItems = useMemo(() => {
    if (!scope?.type) return [];
    return scope.type === '.Visit'
      ? cohorts.reduce(
          (acc, cohort) => {
            acc[cohort.name] = [...cohort.visits].sort(
              (a: VisitConfig, b: VisitConfig) =>
                a.daysFromBaseline - b.daysFromBaseline
            );
            return acc;
          },
          {} as Record<string, VisitConfig[]>
        )
      : cohorts;
  }, [cohorts, scope?.type]);

  const isAllChecked = useMemo(() => {
    if (!scope?.type) return false;
    return scope?.type === '.Patient'
      ? cohorts.length === scope?.cohorts?.length
      : scope.allRegular;
  }, [cohorts.length, scope?.cohorts?.length, scope?.type, scope.allRegular]);

  const handleUnscheduled = (e: CheckboxChangeEvent) => {
    setValue('scope', { ...scope, unscheduled: e.target.checked });
  };
  const handleEarlyTermination = (e: CheckboxChangeEvent) => {
    setValue('scope', { ...scope, earlyTermination: e.target.checked });
  };

  const toggleAllCohorts = (e: CheckboxChangeEvent) => {
    setValue('scope', {
      ...scope,
      cohorts: e.target.checked ? cohorts.map(el => el.id) : [],
    });
  };

  const toggleAllVisits = (e: CheckboxChangeEvent) => {
    setValue('scope.allRegular', e.target.checked);
    if (e.target.checked) {
      setValue('scope.visitDefinitions', []);
    }
  };
  useEffect(() => {
    if (scope?.type && formType === 'create') {
      setValue('scope', scopesFactory(scope?.type));
    }
  }, []);
  return (
    <div>
      <h4 className='font-semibold text-[2.4rem] mb-3'>Definition Scope</h4>
      <div className='bg-blueTone p-3 ps-2 rounded-sm'>
        <div>
          <div className={'grid grid-cols-[150px_1fr] gap-4'}>
            <div
              className={
                'text-1424 font-semibold field-required mt-2.5 max-w-[758px] w-[758px]'
              }
            >
              Scope
            </div>
            <div className={'flex'}>
              <div>
                <Select
                  required={true}
                  showSearch={false}
                  filterOption={(input, option) =>
                    ((option?.label ?? '') as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  listHeight={160}
                  options={scopes}
                  value={scope?.type}
                  onChange={value => setValue('scope', scopesFactory(value))}
                  placeholder={'Select Scope'}
                  classNameLabel='pl-1.5'
                  className={'min-w-[41.2rem]'}
                  classNameInputWrapper={'flex'}
                  child={
                    scope?.type && (
                      <div className={'flex'}>
                        <label className='flex items-center gap-1 whitespace-nowrap ms-3'>
                          <Checkbox
                            onChange={
                              scope?.type === '.Patient'
                                ? toggleAllCohorts
                                : toggleAllVisits
                            }
                            checked={isAllChecked}
                          />
                          <span className={'font-medium text-1420 h-auto'}>
                            {scope?.type === '.Patient'
                              ? 'Select all Cohorts'
                              : 'Select all Regular Visits'}
                          </span>
                        </label>

                        {scope?.type === '.Visit' && (
                          <label className='flex items-center gap-1 whitespace-nowrap ms-3'>
                            <Checkbox
                              onChange={handleUnscheduled}
                              checked={scope?.unscheduled}
                            />
                            <span className={'font-medium text-1420 h-auto'}>
                              Unscheduled
                            </span>
                          </label>
                        )}
                        {scope?.type === '.Visit' && (
                          <label className='flex items-center gap-1 whitespace-nowrap ms-3'>
                            <Checkbox
                              onChange={handleEarlyTermination}
                              checked={scope?.earlyTermination}
                            />
                            <span className={'font-medium text-1420 h-auto'}>
                              Early Termination
                            </span>
                          </label>
                        )}
                      </div>
                    )
                  }
                />
                {scope?.type && <ScopeItemsList items={scopeItems} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
